import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";

import NotFound from "./pages/NotFound";
import Feedbacks from "./pages/Feedbacks";
import Auth from "./pages/Auth";

import { HelmetProvider } from "react-helmet-async";
import { Header } from "./components/layout/Header";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { useEffect, useState, Suspense } from "react"; // Import Suspense
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "@/integrations/firebase/client";
import { ThemeProvider } from "next-themes";
import "@/i18n"; // Import i18n configuration

const queryClient = new QueryClient();

const LoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

const RootRedirect = () => {
  const [loading, setLoading] = useState(true);
  const [isAuthed, setIsAuthed] = useState<boolean | null>(null);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      setIsAuthed(!!user);
      setLoading(false);
    });
    return () => unsub();
  }, []);

  if (loading) {
    return <LoadingFallback />;
  }

  return <Navigate to={isAuthed ? "/feedbacks" : "/auth"} replace />;
};

const AppShell = () => {
  const location = useLocation();
  const showHeader = location.pathname !== "/auth";

  return (
    <>
      {showHeader && <Header />}
      <Routes>
        <Route path="/" element={<RootRedirect />} />
        <Route path="/auth" element={<Auth />} />
        <Route path="/feedbacks" element={<ProtectedRoute><Feedbacks /></ProtectedRoute>} />
        {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <HelmetProvider>
          <Suspense fallback={<LoadingFallback />}>
            <BrowserRouter>
              <AppShell />
            </BrowserRouter>
          </Suspense>
        </HelmetProvider>
      </TooltipProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;

